import React from 'react';
import PropTypes from 'prop-types';
import GuestBilling from '../GuestBilling';

const PlanDetailsCard = ({
  servicePlan,
  paymentInfo,
  paymentLoading,
  monthlyAvailable,
  onChangePlan,
  onPrepay,
  onCancel,
  onPayNow,
  onChangeToMonthly,
}) => {
  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    return new Date(timestamp * 1000).toLocaleDateString();
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '-';
    const num = Number(amount);
    if (Number.isNaN(num)) return '-';
    return `$${num.toFixed(2)}`;
  };

  const isBaseTier = !(servicePlan && servicePlan.id);

  // eslint-disable-next-line no-nested-ternary
  const planName = servicePlan && servicePlan.subscriptionTier && servicePlan.subscriptionTier.name
    ? servicePlan.subscriptionTier.name
    : (isBaseTier ? 'Base' : '-');

  const recurringAmount = servicePlan && servicePlan.recurringAmount !== undefined
    ? formatCurrency(servicePlan.recurringAmount)
    : '-';

  const recurringInterval = servicePlan && servicePlan.recurringInterval
    ? servicePlan.recurringInterval
    : '-';

  const renewalDate = servicePlan && servicePlan.renewalDate
    ? formatDate(servicePlan.renewalDate)
    : '-';

  const isYearlyBilling = servicePlan
    && servicePlan.recurringInterval
    && servicePlan.recurringInterval.toLowerCase() !== 'monthly';

  const showChangeToMonthly = monthlyAvailable && isYearlyBilling;

  let credit = null;

  if (servicePlan) {
    const planCredit = Number(servicePlan.credit) || 0;
    if (planCredit > 0) {
      credit = formatCurrency(planCredit);
    }
  }
  const hasAmountDue = paymentInfo
    && paymentInfo.amountDue !== undefined
    && paymentInfo.amountDue !== null
    && Number(paymentInfo.amountDue) > 0;

  const isPaymentDue = () => {
    if (!paymentInfo) return false;
    if (servicePlan && servicePlan.pastDue === true) return true;
    return !paymentInfo.hasPaymentMethod;
  };

  const dueAmount = paymentInfo && paymentInfo.amountDue !== undefined
    ? formatCurrency(paymentInfo.amountDue)
    : formatCurrency(servicePlan && servicePlan.recurringAmount);

  const dueDate = servicePlan && servicePlan.nextPaymentDue
    ? formatDate(servicePlan.nextPaymentDue)
    : '-';

  return (
    <>
      <div className="col-md-8 mb-4">
        <div className="card mb-3">
          <div className="card-header">
            <strong>Current Plan</strong>
          </div>

          <div className="card-body p-0">

            <div className="row align-items-center px-3 py-2">
              <div className="col-3">
                <strong>Level</strong>
              </div>

              <div className="col-6">
                {planName}
                <a
                  href={`${window.envConfig.raaUrl || ''}/pricing`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-link btn-sm p-0 ml-2"
                >
                  Compare plans
                </a>
              </div>

              <div className="col-3 text-right">
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={onChangePlan}
                >
                  Change Plan
                </button>
              </div>
            </div>

            {!isBaseTier && (
              <>
                <div className="row align-items-center px-3 py-2">
                  <div className="col-3">
                    <strong>Renews at</strong>
                  </div>

                  <div className="col-6">
                    {recurringAmount}
                    {' '}
                    /
                    {recurringInterval}
                  </div>

                  <div className="col-3 text-right">
                    {showChangeToMonthly && (
                      <button
                        type="button"
                        className="btn btn-link btn-sm p-0"
                        onClick={onChangeToMonthly}
                      >
                        {isYearlyBilling ? 'Change to monthly' : 'Change to annual'}
                      </button>
                    )}
                  </div>
                </div>

                <div className="row align-items-center px-3 py-2">
                  <div className="col-3">
                    <strong>Renews on</strong>
                  </div>

                  <div className="col-6">
                    {renewalDate}
                  </div>

                  <div className="col-3 text-right">
                    <button type="button" className="btn btn-link btn-sm p-0" onClick={onPrepay}>
                      Prepay
                    </button>
                    {' | '}
                    <button type="button" className="btn btn-link btn-sm p-0" onClick={onCancel}>
                      Cancel
                    </button>
                  </div>
                </div>

                {credit && (
                  <div className="row align-items-center px-3 py-2">
                    <div className="col-3">
                      <strong>Credit</strong>
                    </div>
                    <div className="col-6">{credit}</div>
                    <div className="col-3" />
                  </div>
                )}

                {!paymentLoading && hasAmountDue && (
                  <div className="px-3 pb-3">
                    {isPaymentDue() ? (
                      <div className="alert alert-danger d-flex justify-content-between align-items-center mt-2">
                        <strong>
                          {dueAmount}
                          {' '}
                          due on
                          {' '}
                          {dueDate}
                        </strong>

                        <button
                          type="button"
                          className="btn btn-danger btn-sm"
                          onClick={onPayNow}
                        >
                          Pay Now
                        </button>
                      </div>
                    ) : (
                      <div className="alert alert-success mb-0 mt-2">
                        <strong>
                          {dueAmount}
                          {' '}
                          will be charged automatically on
                          {' '}
                          {dueDate}
                        </strong>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

          </div>
        </div>
        <GuestBilling />
      </div>
    </>
  );
};

PlanDetailsCard.propTypes = {
  servicePlan: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    description: PropTypes.string,
    recurringAmount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    recurringInterval: PropTypes.string,
    renewalDate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    nextPaymentDue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    credit: PropTypes.number,
    pastDue: PropTypes.bool,
    subscriptionTier: PropTypes.shape({
      name: PropTypes.string,
    }).isRequired,
  }),

  paymentInfo: PropTypes.shape({
    hasPaymentMethod: PropTypes.bool,
    amountDue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    dueDate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),

  paymentLoading: PropTypes.bool,

  onChangePlan: PropTypes.func.isRequired,
  onPrepay: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onPayNow: PropTypes.func.isRequired,
  onChangeToMonthly: PropTypes.func.isRequired,
  monthlyAvailable: PropTypes.bool.isRequired,
};

PlanDetailsCard.defaultProps = {
  servicePlan: null,
  paymentInfo: null,
  paymentLoading: false,
};

export default PlanDetailsCard;
